import React from 'react';
import { cn } from '../../../lib/utils';

export interface InfoFieldProps {
  /** The label for the information field. */
  label: string;
  /** The value to display. Can be simple text, a number, or a React node (e.g., a StatusPill). */
  value: React.ReactNode;
  /** Optional CSS classes for the container div wrapping both label and value. */
  containerClassName?: string;
  /** Optional CSS classes for the label element. */
  labelClassName?: string;
  /** Optional CSS classes for the value element. */
  valueClassName?: string;
  /** Determines the orientation of label and value. Defaults to 'vertical'. */
  orientation?: 'vertical' | 'horizontal';
}

/**
 * InfoField Component
 * Displays a label and its corresponding value.
 * Supports vertical (default) or horizontal orientation.
 */
export const InfoField: React.FC<InfoFieldProps> = ({
  label,
  value,
  containerClassName,
  labelClassName,
  valueClassName,
  orientation = 'vertical',
}) => {
  if (orientation === 'horizontal') {
    return (
      <div className={cn("flex items-center justify-between", containerClassName)}>
        {label &&
          <dt className={cn("text-sm font-normal text-gray-600", labelClassName)}>
            {label}
          </dt>
        }
        <dd className={cn("text-sm font-semibold text-gray-900 break-words text-right", valueClassName)}>
          {value || 'N/A'} {/* Display N/A if value is empty/null/undefined */}
        </dd>
      </div>
    );
  }

  // Default: vertical orientation
  return (
    <div className={cn(containerClassName)}>
      {label &&
        <dt className={cn(
          "text-sm font-medium text-gray-600 mb-1", // Default vertical label style
          labelClassName
        )}>
          {label}
        </dt>
      }
      <dd className={cn(
        "text-sm font-semibold text-gray-900 break-words", // Default vertical value style
        valueClassName
      )}>
        {value !== null && value !== undefined && value !== '' ? value : <span className="text-gray-900/70 italic">N/A</span>}
      </dd>
    </div>
  );
};